import {
  Psychology as KnowledgeBaseIcon,
  Logout as LogoutIcon,
  IntegrationInstructionsOutlined as PromptIcon,
  QuestionAnswer as QuestionAnswerIcon,
  SettingsApplicationsOutlined,
  DynamicForm as DynamicFormIcon,
} from '@mui/icons-material';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  SwipeableDrawer,
  ThemeProvider,
  Toolbar,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { FC } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { useAuth } from 'lib/auth/AuthContext';
import { darkSubTheme } from 'theme';
import { capitalize } from 'lodash-es';
import { useGetKnowledgeBases } from 'features/embeddings';
import config from 'config';
import SidebarNavItem from '../../SidebarNavItem';

const HomeLink = styled(Link)`
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  object-fit: contain;
  align-self: center;
  width: 100%;
  height: 100%;
  font-size: 20px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.2em;
`;

const EnvLabel = styled(Box)`
  z-index: 100;
  color: black;
  text-align: center;
  font-weight: 600;
  top: 0;
  left: 0;
  right: 0;
  font-size: 12px;
  padding: 1px 6px;
  margin-left: 5px;
  border-radius: 12px;
  background-color: #efef62;
  text-transform: uppercase;
  letter-spacing: initial;
`;

interface SidebarProps {
  isShown: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

const Sidebar: FC<SidebarProps> = ({ isShown, openDrawer, closeDrawer }) => {
  const theme = useTheme();
  const isKbPage = useMatch('/knowledge-bases/*');
  const isStage = config.env.ENVIRONMENT === 'stage';

  const { data: kbs } = useGetKnowledgeBases();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  const handleSignOut = () => {
    signOut();
  };

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        pt: 1.3,
      }}
    >
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <HomeLink to="/">
          {config.title}
          {isStage && <EnvLabel>stage</EnvLabel>}
        </HomeLink>
      </Toolbar>

      <Box component="nav" sx={{ flexGrow: 1 }}>
        <List>
          <SidebarNavItem
            to={`/knowledge-bases`}
            icon={<KnowledgeBaseIcon />}
            title={<Typography noWrap>Knowledge Bases</Typography>}
            initialExpanded={!!isKbPage}
          >
            {kbs?.map((knowledgeBase) => (
              <SidebarNavItem
                key={knowledgeBase}
                to={`/knowledge-bases/${knowledgeBase}`}
                title={capitalize(knowledgeBase)}
              />
            ))}
          </SidebarNavItem>
          <SidebarNavItem to={`/prompts`} icon={<PromptIcon />} title="Prompts" />
          <SidebarNavItem to={`/ask-me-something`} icon={<QuestionAnswerIcon />} title="Q&A" />
          <SidebarNavItem to={`/frontapp`} icon={<SettingsApplicationsOutlined />} title="Frontapp" />
          <SidebarNavItem to={`/contact-form`} icon={<DynamicFormIcon />} title="Contact Form" />
        </List>
      </Box>
      <Box sx={{ py: 3 }}>
        <List>
          <ListItem>
            <Divider />
            <ListItemText
              primary={user?.name}
              secondary={user?.email}
              secondaryTypographyProps={{ noWrap: true }}
              sx={(t) => ({ padding: `${t.spacing(2)} 0 0`, wordBreak: 'break-all' })}
            />
          </ListItem>
          <SidebarNavItem icon={<LogoutIcon />} onClick={handleSignOut} title="Sign Out" />
        </List>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <ThemeProvider theme={darkSubTheme}>
        <SwipeableDrawer
          variant="temporary"
          sx={{ zIndex: theme.zIndex.appBar - 1, maxHeight: '100vh' }}
          PaperProps={{
            sx: {
              height: 'unset',
              position: 'absolute',
              bottom: 0,
              width: '100%',
              maxWidth: theme.sizes.sidebarWidth,
              [theme.breakpoints.down('md')]: {
                // top: 65,
                maxWidth: '40%',
              },
              [theme.breakpoints.down('sm')]: {
                // top: 57,
                maxWidth: '100vw',
              },
            },
          }}
          componentsProps={{
            backdrop: () => ({
              sx: {
                [theme.breakpoints.down('md')]: {
                  top: 65,
                },
                [theme.breakpoints.down('sm')]: {
                  top: 57,
                },
              },
            }),
          }}
          open={isShown}
          onOpen={openDrawer}
          onClose={closeDrawer}
        >
          <Toolbar />
          {content}
        </SwipeableDrawer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkSubTheme}>
      <Drawer
        variant="permanent"
        sx={{
          flexShrink: 0,
          width: theme.sizes.sidebarWidth,
        }}
        PaperProps={{
          sx: {
            width: theme.sizes.sidebarWidth,
          },
        }}
      >
        {content}
      </Drawer>
    </ThemeProvider>
  );
};

export default Sidebar;
